import { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

interface Option {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
}

export default function MultiSelect({ options, value, onChange, placeholder }: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setSearch('');
    }
  }, [isOpen]);

  const handleToggleOption = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const getDisplayValue = () => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) return options.find(opt => opt.value === value[0])?.label ?? value[0];
    return `${value.length} sélectionnés`;
  };

  const filteredOptions = search.trim()
    ? options.filter(opt => opt.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const hasValue = value.length > 0;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 text-left border rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 bg-white ${hasValue ? 'border-indigo-300 text-slate-800' : 'border-slate-200 text-slate-400'}`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="block truncate">{getDisplayValue()}</span>
          <div className="flex items-center gap-1 shrink-0">
            {hasValue && (
              <span
                role="button"
                onClick={e => { e.stopPropagation(); onChange([]); }}
                className="flex items-center justify-center w-4 h-4 rounded-full bg-slate-200 hover:bg-red-100 hover:text-red-500 text-slate-500 transition-colors"
              >
                <X size={10} strokeWidth={2.5} />
              </span>
            )}
            <ChevronDown size={15} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-30 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg">
          <div className="p-2 border-b border-slate-100">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 rounded-lg">
              <Search size={13} className="text-slate-400 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher..."
                className="flex-1 text-sm bg-transparent outline-none text-slate-700 placeholder:text-slate-400"
              />
              {search && (
                <button onClick={() => setSearch('')} className="text-slate-400 hover:text-slate-600 leading-none">✕</button>
              )}
            </div>
          </div>
          <div className="max-h-52 overflow-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-slate-400 italic">Aucun résultat</div>
            ) : (
              filteredOptions.map((option) => {
                const selected = value.includes(option.value);
                return (
                  <div
                    key={option.value}
                    className={`flex items-center justify-between px-3 py-2 cursor-pointer transition-colors ${selected ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}
                    onClick={() => handleToggleOption(option.value)}
                  >
                    <span className={`text-sm ${selected ? 'text-indigo-700 font-medium' : 'text-slate-700'}`}>
                      {option.label}
                    </span>
                    {selected && <Check size={14} className="text-indigo-600 shrink-0" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
